import { createFileRoute, Link } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";
import {
  ArrowRight,
  CheckCircle2,
  Wrench,
  MapPin,
  Clock,
  Zap,
  Truck,
  Award,
  DollarSign,
} from "lucide-react";

const URL = "https://vendingmachinefree.com/about";
const TITLE = "About Us — 20+ Years, 6,000+ Machines Installed";
const DESC =
  "Vending Machine Free.com — 20+ years of vending expertise and 6,000+ installs placing FREE vending machines nationwide across all 50 US states.";

const FAQS = [
  {
    q: "How long has Vending Machine Free.com been in the vending industry?",
    a: "Our founder has been in the vending business for more than 20 years, operating machines, managing route drivers, and supporting owner-operators across the USA.",
  },
  {
    q: "How many vending machines have you installed?",
    a: "We have completed more than 6,000 installations, removals, transports, and relocations of vending machines across all industries and business types nationwide.",
  },
  {
    q: "What types of vending machines do you work with?",
    a: "We handle drink, snack, combo, coffee, food, ice, cigarette & tobacco, PPE, and custom vending machines — from mechanical units to modern cashless systems.",
  },
  {
    q: "Do you really place vending machines for free?",
    a: "Yes. Qualifying businesses across all 50 states receive the machine, delivery, installation, restocking, repairs, and maintenance at $0 cost.",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Vending Machine Free.com",
          url: URL,
          description: DESC,
          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            name: "Vending Machine Free.com",
            url: "https://vendingmachinefree.com",
          },
          mainEntity: {
            "@type": "Organization",
            name: "Vending Machine Free.com",
            url: "https://vendingmachinefree.com",
            slogan: "Powered by American businesses — get a Free Vending Machine Nationwide.",
            areaServed: { "@type": "Country", name: "United States" },
            knowsAbout: [
              "Vending Machine Placement",
              "Free Vending Machines",
              "Vending Machine Service",
              "Cashless Vending Systems",
              "Vending Machine Telemetry",
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Vending Machine Free.com",
          url: "https://vendingmachinefree.com",
          logo: "https://vendingmachinefree.com/favicon-512.png",
          slogan: "Powered by American businesses — get a Free Vending Machine Nationwide.",
          areaServed: { "@type": "Country", name: "United States" },
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            areaServed: "US",
            availableLanguage: ["English"],
            url: "https://vendingmachinefree.com/#contact",
            contactOption: "OnlineChat",
          },
          sameAs: [
            "https://www.facebook.com/vendingmachinesfree",
            "https://www.linkedin.com/company/vendingmachines-free",
            "https://www.instagram.com/vendingmachinesfree",
            "https://twitter.com/vending_free",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://vendingmachinefree.com/",
            },
            { "@type": "ListItem", position: 2, name: "About Us", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

const MACHINE_TYPES = [
  "Drink vending machines",
  "Snack vending machines",
  "Combo vending machines",
  "Coffee vending machines",
  "Food vending machines",
  "Ice vending machines",
  "Cigarette & tobacco vending machines",
  "PPE vending machines",
  "Custom vending machines",
];

const TECH_ISSUES = [
  "Fuse failures",
  "Door switch faults",
  "Compressor problems",
  "Fan noise",
  "Control board failures",
  "Starter caps",
  "Temperature issues",
  "Coin mech & note reader faults",
];

const OPERATOR_SERVICES = [
  "Getting new sites for vending machine placement",
  "Helping operators secure high-traffic locations",
  "Providing tools to increase sales",
  "Supporting operators with backend systems",
  "Offering remote monitoring solutions",
  "Delivering marketing materials and placement leads",
];

const NATIONWIDE_PERKS = [
  { icon: DollarSign, text: "$0 installation" },
  { icon: Wrench, text: "$0 servicing" },
  { icon: Truck, text: "$0 repairs" },
  { icon: CheckCircle2, text: "$0 restocking" },
  { icon: Zap, text: "Modern cashless machines" },
  { icon: MapPin, text: "Remote monitoring" },
  { icon: Clock, text: "Fast operator response" },
  { icon: Award, text: "All 50 states covered" },
];

function Page() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section
        className="relative overflow-hidden text-steel-foreground"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="absolute inset-0 stripes-bg opacity-30" aria-hidden />
        <div className="container-x relative py-12 sm:py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-safety" /> 20+ Years · 6,000+ Machines ·
              Nationwide USA
            </div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] uppercase">
              About Us — 20+ Years of Vending Experience, 6,000+ Machines Installed Nationwide
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/85 max-w-2xl">
              From running 134 sited machines to building one of the earliest vending
              lead-generation networks in the USA, we have lived and breathed vending for more than
              two decades.
            </p>
            <Link
              to="/"
              hash="contact"
              onClick={() => trackApplyClick("about_hero")}
              className="inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition mt-8"
            >
              Apply for a Free Vending Machine <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Intro / Lifetime */}
      <section className="py-16">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl uppercase">A Lifetime in Vending</h2>
          <p className="mt-4 text-lg text-muted-foreground">
            For more than 20 years, our founder has been deeply involved in the vending industry —
            running 134 sited machines, managing two full-time fillers, and operating one of the
            earliest online vending lead-generation networks for vending owner-operators across the
            USA.
          </p>
          <p className="mt-4 text-lg text-muted-foreground">
            From the early days of mechanical snack machines to today’s modern cashless systems,
            we’ve been hands-on with every type of vending machine.
          </p>
        </div>
      </section>

      {/* Machine Types */}
      <section className="py-16 bg-muted">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            Vending Machines We Know Inside & Out
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {MACHINE_TYPES.map((m) => (
              <div
                key={m}
                className="flex items-center gap-3 bg-background border border-border p-4 rounded-md"
              >
                <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0" />
                <span className="font-medium">{m}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-muted-foreground max-w-2xl mx-auto">
            Over the years, we’ve completed more than 6,000 installations, removals, transports, and
            relocations of vending machines across all industries and business types.
          </p>
        </div>
      </section>

      {/* Technical Expertise */}
      <section className="py-16">
        <div className="container-x grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl uppercase">
              Technical Expertise That Operators Trust
            </h2>
            <p className="mt-4 text-muted-foreground">
              Long before credit card readers became standard, we were involved in early telemetry
              systems — helping operators monitor stock levels, machine temperatures, compressor
              performance, and sales data remotely.
            </p>
            <p className="mt-4 text-muted-foreground">
              We’ve diagnosed thousands of machine issues and worked directly with techs nationwide,
              guiding them through real-world vending repairs and troubleshooting.
            </p>
          </div>
          <div className="bg-muted p-6 rounded-md">
            <h3 className="font-display text-xl uppercase mb-4">Issues We’ve Troubleshot</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TECH_ISSUES.map((issue) => (
                <li key={issue} className="flex items-center gap-2 text-sm">
                  <Wrench className="h-4 w-4 text-safety flex-shrink-0" /> {issue}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Helping Operators Grow */}
      <section className="py-16 bg-steel text-steel-foreground">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            Helping Operators Grow
          </h2>
          <p className="mt-4 text-center text-white/80 max-w-2xl mx-auto">
            Our background includes supporting vending operators with everything from site
            acquisition to sales growth and backend systems.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {OPERATOR_SERVICES.map((s) => (
              <div key={s} className="bg-white/10 border border-white/10 p-4 rounded-md">
                <div className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-safety" />
                  <span className="font-medium">{s}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-white/80 font-semibold">
            We understand what operators need — because we’ve lived it.
          </p>
        </div>
      </section>

      {/* Nationwide Placement */}
      <section className="py-16">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            Nationwide Placement Network
          </h2>
          <p className="mt-4 text-center text-muted-foreground max-w-2xl mx-auto">
            Today, we help businesses across all 50 states get FREE vending machines with a fully
            managed, zero-cost program.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {NATIONWIDE_PERKS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 bg-muted p-4 rounded-md">
                <Icon className="h-5 w-5 text-safety flex-shrink-0" />
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-muted-foreground max-w-2xl mx-auto">
            Whether you’re a warehouse, office, school, hospital, hotel, gym, or retail business —
            we match your location with the right operator.
          </p>
        </div>
      </section>

      {/* Motto + CTA */}
      <section className="py-16 bg-muted">
        <div className="container-x max-w-3xl text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase">Our Motto</h2>
          <blockquote className="mt-6 text-2xl md:text-3xl font-display italic text-foreground">
            “It’s never ending vending — because vending is never ending.”
          </blockquote>
          <div className="mt-10 bg-background border border-border p-8 rounded-md">
            <h3 className="font-display text-2xl uppercase text-safety">
              Apply for Vending Machine Free — $0 Cost
            </h3>
            <p className="mt-3 text-muted-foreground">
              Get a fully managed vending machine at no cost to your business. Installation,
              restocking, repairs, and service are all included.
            </p>
            <Link
              to="/"
              hash="contact"
              onClick={() => trackApplyClick("about_footer")}
              className="inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition mt-6"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            Frequently Asked Questions
          </h2>
          <div className="mt-8 space-y-6">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-border pb-6">
                <h3 className="font-semibold text-lg">{f.q}</h3>
                <p className="mt-2 text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-steel text-steel-foreground text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase">
            Ready for a Free Vending Machine?
          </h2>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">
            Join thousands of businesses across the USA that trust Vending Machine Free.com for
            hassle-free vending placement.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("about_final")}
            className="inline-flex items-center gap-2 rounded-md bg-safety px-8 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition mt-8"
          >
            Apply for Your Free Vending Machine <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
